import gsap from "gsap";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { PackHorse, PackPurchase } from "@/api";

import { playRipSound, playRipWin, preloadRipAudio } from "./audio";
import { runQuickRipIntro, spawnCardBurst, type IntroHandle } from "./motion";
import { resolvePresentation, TIER_ORDER, type RipTier } from "./presentation";

/** The counter counts REAL money: the open window's instant quote (older
 * deliveries without one fall back to the face value). */
function horseValueCents(horse: PackHorse): number {
  return horse.instantQuoteCents ?? horse.stickerCents;
}

export type RipPhase = "intro" | "reveal" | "summary";
export type SlotPhase =
  "sealed" | "sweat" | "turning" | "upgrading" | "revealed";

export type SlotVisual = {
  phase: SlotPhase;
  faceVisible: boolean;
  visibleTier: RipTier;
  rainbow: boolean;
};

const NEXT_PACK_PAYOFF_CADENCE_MS = 1_600;
const SUMMARY_PAYOFF_CADENCE_MS = 500;
// The full intro (fly-in, shakes, pop, fan) completes in ~3s; the 3.5s
// fallback pops a slow pack. Past this point the choreography has stalled.
const INTRO_HARD_HANDOFF_MS = 7_000;

function delay(milliseconds: number) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function tween(target: gsap.TweenTarget, vars: gsap.TweenVars) {
  return new Promise<void>((resolve) => {
    gsap.to(target, { ...vars, onComplete: resolve });
  });
}

export function useQuickRipController({
  purchases,
  initialIndex,
  instant,
  onIndexChange,
}: {
  purchases: PackPurchase[];
  initialIndex: number;
  instant: boolean;
  onIndexChange?: (index: number) => void;
}) {
  const reducedMotion = useMemo(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );
  const [index, setIndex] = useState(initialIndex);
  const purchase = purchases[index];
  const presentation = useMemo(
    () => resolvePresentation(purchase, reducedMotion),
    [purchase, reducedMotion],
  );
  const [phase, setPhase] = useState<RipPhase>(
    instant ? "summary" : reducedMotion ? "reveal" : "intro",
  );
  const [fanStarted, setFanStarted] = useState(instant || reducedMotion);
  const [fanLanded, setFanLanded] = useState(instant || reducedMotion);
  const initialRevealed = instant
    ? new Set(purchase.contents.map((_, itemIndex) => itemIndex))
    : new Set<number>();
  const [revealed, setRevealed] = useState<Set<number>>(initialRevealed);
  const revealedRef = useRef(initialRevealed);
  const [slots, setSlots] = useState<SlotVisual[]>(() =>
    presentation.beats.map((beat) => ({
      phase: instant ? "revealed" : "sealed",
      faceVisible: instant,
      visibleTier: instant ? beat.actualTier : beat.shownTier,
      rainbow: instant ? beat.rainbow : false,
    })),
  );
  const [displayedValue, setDisplayedValue] = useState(
    instant
      ? purchase.contents.reduce(
          (total, horse) => total + horseValueCents(horse),
          0,
        )
      : 0,
  );
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const payoffStartedAt = useRef<number | null>(null);
  const introHandle = useRef<IntroHandle | null>(null);
  const packRef = useRef<HTMLButtonElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const flashRef = useRef<HTMLDivElement>(null);
  const slotRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const counter = useRef({ value: displayedValue });

  const updateSlot = useCallback(
    (contentIndex: number, patch: Partial<SlotVisual>) => {
      setSlots((current) =>
        current.map((slot, indexValue) =>
          indexValue === contentIndex ? { ...slot, ...patch } : slot,
        ),
      );
    },
    [],
  );

  const resetPack = useCallback(
    (nextIndex: number) => {
      const nextPurchase = purchases[nextIndex];
      const nextPresentation = resolvePresentation(nextPurchase, reducedMotion);
      const all = instant
        ? new Set(nextPurchase.contents.map((_, contentIndex) => contentIndex))
        : new Set<number>();
      revealedRef.current = all;
      setRevealed(all);
      setSlots(
        nextPresentation.beats.map((beat) => ({
          phase: instant ? "revealed" : "sealed",
          faceVisible: instant,
          visibleTier: instant ? beat.actualTier : beat.shownTier,
          rainbow: instant ? beat.rainbow : false,
        })),
      );
      const nextValue = instant
        ? nextPurchase.contents.reduce(
            (total, horse) => total + horseValueCents(horse),
            0,
          )
        : 0;
      counter.current.value = nextValue;
      setDisplayedValue(nextValue);
      payoffStartedAt.current = null;
      setFanStarted(instant || reducedMotion);
      setFanLanded(instant || reducedMotion);
      setPhase(instant ? "summary" : reducedMotion ? "reveal" : "intro");
      setBusy(false);
      busyRef.current = false;
    },
    [instant, purchases, reducedMotion],
  );

  useEffect(() => {
    void preloadRipAudio();
  }, []);

  useEffect(() => {
    if (phase !== "intro") return;
    const handle = runQuickRipIntro({
      pack: packRef.current,
      slots: slotRefs.current.filter((slot): slot is HTMLButtonElement =>
        Boolean(slot),
      ),
      glow: glowRef.current,
      flash: flashRef.current,
      reducedMotion,
      onFanStart: () => setFanStarted(true),
      onFanLanded: () => setFanLanded(true),
    });
    introHandle.current = handle;
    void handle.finished.then(() => setPhase("reveal"));
    const fallback = window.setTimeout(() => {
      if (introHandle.current === handle) {
        handle.pop();
      }
    }, 3_500);
    // Hard handoff: the intro is choreography, never a gate. If the tween
    // clock stalls (a suspended or background webview, a throttled ticker)
    // the pack must still open — otherwise the player is stuck on a sealed
    // pack they have already paid for, with every card disabled.
    const hardHandoff = window.setTimeout(() => {
      if (introHandle.current !== handle) return;
      handle.kill();
      introHandle.current = null;
      gsap.set(packRef.current, { opacity: 0 });
      gsap.set(
        slotRefs.current.filter((slot): slot is HTMLButtonElement =>
          Boolean(slot),
        ),
        { clearProps: "transform,opacity,willChange" },
      );
      setFanStarted(true);
      setFanLanded(true);
      setPhase("reveal");
    }, INTRO_HARD_HANDOFF_MS);
    return () => {
      window.clearTimeout(fallback);
      window.clearTimeout(hardHandoff);
      handle.kill();
      if (introHandle.current === handle) introHandle.current = null;
    };
  }, [index, phase, reducedMotion]);

  const animateValue = useCallback(
    (nextValue: number) => {
      if (reducedMotion) {
        counter.current.value = nextValue;
        setDisplayedValue(nextValue);
        return;
      }
      gsap.to(counter.current, {
        value: nextValue,
        duration: 0.48,
        ease: "power2.out",
        onUpdate: () => setDisplayedValue(Math.round(counter.current.value)),
      });
    },
    [reducedMotion],
  );

  const revealCard = useCallback(
    async (contentIndex: number) => {
      if (
        revealedRef.current.has(contentIndex) ||
        busyRef.current ||
        phase !== "reveal"
      ) {
        return;
      }
      busyRef.current = true;
      setBusy(true);
      const beat = presentation.beats[contentIndex];
      const displayIndex = presentation.order.indexOf(contentIndex);
      const element = slotRefs.current[displayIndex];
      const flipNode =
        element?.querySelector<HTMLElement>(".rip-card__inner") ?? null;
      const opacityFlip =
        window.matchMedia("(pointer: coarse)").matches &&
        /AppleWebKit/i.test(navigator.userAgent);

      if (!reducedMotion && beat.rainbow && flipNode) {
        updateSlot(contentIndex, { phase: "sweat", rainbow: true });
        void playRipSound("rainbow_hit", 1);
        await tween(flipNode, {
          scale: 1.12,
          filter: "brightness(1.8) saturate(1.5)",
          duration: 0.9,
          ease: "power2.in",
        });
        spawnCardBurst(element ?? flipNode, true);
      } else if (!reducedMotion && beat.sweatLevel > 0.05 && flipNode) {
        updateSlot(contentIndex, { phase: "sweat" });
        void playRipSound("sweat_whoosh", 0.45 + beat.sweatLevel * 0.5);
        await tween(flipNode, {
          x: beat.sweatLevel >= 0.5 ? 6 : 3,
          scale: 1 + beat.sweatLevel * 0.08,
          duration: 0.055,
          repeat: Math.round(3 + beat.sweatLevel * 8),
          yoyo: true,
          ease: "none",
        });
        gsap.set(flipNode, { x: 0 });
      }

      updateSlot(contentIndex, {
        phase: "turning",
        faceVisible: true,
        visibleTier: beat.shownTier,
      });
      await delay(reducedMotion ? 0 : 16);
      if (!reducedMotion && flipNode) {
        await new Promise<void>((resolve) => {
          if (opacityFlip) {
            gsap.fromTo(
              flipNode,
              { opacity: 0 },
              {
                opacity: 1,
                duration: 0.32,
                ease: "power2.out",
                onComplete: resolve,
              },
            );
          } else {
            gsap.fromTo(
              flipNode,
              { rotateY: 0, scale: 0.94 },
              {
                rotateY: 180,
                scale: 1,
                duration: 0.42,
                ease: "back.out(1.4)",
                onComplete: resolve,
              },
            );
          }
        });
      }
      // The turn transient and tactile payoff land with the visible face.
      void playRipSound("card_turn", 0.9);

      if (beat.upgradeGap > 0) {
        updateSlot(contentIndex, { phase: "upgrading" });
        let tierIndex = TIER_ORDER.indexOf(beat.shownTier);
        for (let step = 0; step < beat.upgradeGap; step += 1) {
          if (beat.fakeFlipSchedule[step] && flipNode && !opacityFlip) {
            await tween(flipNode, {
              rotateY: 154,
              duration: 0.13,
              repeat: 1,
              yoyo: true,
            });
          }
          tierIndex += 1;
          const visibleTier = TIER_ORDER[tierIndex] ?? beat.actualTier;
          updateSlot(contentIndex, { visibleTier });
          void playRipSound("pack_pop", 0.45 + step * 0.12);
          if (element && flipNode) {
            spawnCardBurst(element, false);
            await tween(flipNode, {
              scale: 1.08,
              duration: 0.16,
              repeat: 1,
              yoyo: true,
              ease: "power2.out",
            });
          }
        }
      }

      updateSlot(contentIndex, {
        phase: "revealed",
        faceVisible: true,
        visibleTier: beat.actualTier,
        rainbow: beat.rainbow,
      });
      if (element) spawnCardBurst(element, beat.rainbow);
      playRipWin(
        beat.sweatLevel,
        contentIndex === presentation.hitIndex ? 1 : 0.72,
      );
      payoffStartedAt.current = performance.now();
      const nextRevealed = new Set(revealedRef.current);
      nextRevealed.add(contentIndex);
      revealedRef.current = nextRevealed;
      setRevealed(nextRevealed);
      animateValue(
        [...nextRevealed].reduce(
          (total, indexValue) =>
            total + horseValueCents(purchase.contents[indexValue]),
          0,
        ),
      );
      busyRef.current = false;
      setBusy(false);

      if (nextRevealed.size === purchase.contents.length) {
        const elapsed = performance.now() - (payoffStartedAt.current ?? 0);
        await delay(
          reducedMotion ? 0 : Math.max(0, SUMMARY_PAYOFF_CADENCE_MS - elapsed),
        );
        setPhase("summary");
      }
    },
    [
      animateValue,
      phase,
      presentation.beats,
      presentation.hitIndex,
      presentation.order,
      purchase.contents,
      reducedMotion,
      updateSlot,
    ],
  );

  const revealRemaining = useCallback(async () => {
    for (const contentIndex of presentation.order) {
      if (!revealedRef.current.has(contentIndex)) {
        await revealCard(contentIndex);
        await delay(reducedMotion ? 0 : 120);
      }
    }
  }, [presentation.order, reducedMotion, revealCard]);

  const nextPack = useCallback(async () => {
    if (index >= purchases.length - 1) return;
    const elapsed = performance.now() - (payoffStartedAt.current ?? 0);
    await delay(
      instant ? 0 : Math.max(0, NEXT_PACK_PAYOFF_CADENCE_MS - elapsed),
    );
    const nextIndex = index + 1;
    setIndex(nextIndex);
    onIndexChange?.(nextIndex);
    resetPack(nextIndex);
  }, [index, instant, onIndexChange, purchases.length, resetPack]);

  const setSlotRef = useCallback(
    (displayIndex: number, element: HTMLButtonElement | null) => {
      slotRefs.current[displayIndex] = element;
    },
    [],
  );
  const popIntro = useCallback(() => introHandle.current?.pop(), []);

  return {
    index,
    purchase,
    presentation,
    phase,
    fanStarted,
    fanLanded,
    slots,
    revealed,
    displayedValue,
    busy,
    packRef,
    glowRef,
    flashRef,
    setSlotRef,
    popIntro,
    revealCard,
    revealRemaining,
    nextPack,
  };
}
